import Highcharts from 'highcharts'
import pointService from 'live/services/charts/point'
import { MarkersData } from '../api'
import ChartMarker, { HighchartsMarker } from './chart-marker'
import Clusters from './clusters'
import { ExtendedPlotline } from './extended-plotline'

export type Widget = {
    chart: Highcharts.Chart & {
        widgetId: number
        spanType: string
        pauseTooltip: boolean
        widgetName: string
    }
    options: Record<string, any>
    el: HTMLDivElement
    context: Record<string, any>
    cfg: Record<string, any>
}

export type WidgetTarget = {
    chart: { widgetId?: number; spanType: string }
}

type SpanType = 'time' | string

export default class ServiceMarker {
    /**
     * Where the marker will be applied ?
     *  1. null     -> Nowhere, marker mode is turned off
     *  2. Widget   -> In this unique widget
     *  3. SpanType -> Every widget in the dashboard with this span type
     */
    private __currentTarget: null | WidgetTarget | SpanType
    private __editing: MarkersData | null
    private chartMarker: ChartMarker
    private scheduledMarkers: [number, ExtendedPlotline['options']][] = []
    private onClickMarker: (widgetId: number, data: MarkersData, position: Record<string, unknown>) => void

    constructor(onClickMarker: (widgetId: number, data: MarkersData, position: Record<string, unknown>) => void) {
        this.chartMarker = new ChartMarker()
        this.onClickMarker = onClickMarker
        this.__currentTarget = null
        this.__editing = null

        pointService.currentPoint$.subscribe(this.moveDraftMarker)
    }

    /**
     * Builds a new `HighchartsMarker.options` object with the given params.
     *
     * @param id Marker `id`
     * @param value Marker position (x value)
     * @param widget `id` of the widget where this marker will be applied
     * @returns A `HighchartsMarker.options` for this marker
     */
    private makeMarkerOptions(id: string, value: number, widget: number): HighchartsMarker['options'] {
        let target: { spanType: string; widgetId?: number }

        if (typeof this.__currentTarget === 'string') {
            target = { spanType: this.__currentTarget }
        } else {
            target = {
                widgetId: this.__currentTarget!.chart?.widgetId,
                spanType: this.__currentTarget!.chart?.spanType
            }
        }

        return {
            id: id,
            value: value,
            data: {
                id: id,
                target: target,
                value: value,
                widgets: []
            },
            onShowDetails: this.onShowDetails(target, widget),
            onChangePosition: () => null
        }
    }

    private onShowDetails = (
        target: ExtendedPlotline['options']['data']['target'],
        widget: number
    ): ExtendedPlotline['options']['onShowDetails'] => {
        // Show the most up to date data in the details modal.
        return (data, pos) => {
            const charts = normalizeTarget(target) || []
            // Series inside this marker. If this is a global marker
            // it may contain series of others charts in the dashboard.
            const widgetsSeries: ExtendedPlotline['options']['data']['widgets'] = []
            let last: HighchartsMarker | null = null

            for (const chart of charts) {
                const mkr = this.chartMarker.findMarker(chart, data.id)

                if (!mkr) continue

                const mkrCurve = mkr.options.data.widgets.find(a => a?.widgetId === chart.widgetId)

                widgetsSeries.push(mkrCurve)

                last = mkr
            }

            if (last != null) {
                last.options.data.widgets = widgetsSeries

                this.onClickMarker(widget, last.options.data, pos)
            }
        }
    }

    /**
     * Normalizes the `this.__currentTarget` object to an array of charts
     * where the marker should be applied.
     *
     * @returns A list of all widgets where the marker will be applied.
     */
    get currentTarget(): Widget['chart'][] {
        const target = this.__currentTarget
        let charts: Widget['chart'][] = Highcharts.charts as Widget['chart'][]

        if (typeof target === 'string') {
            charts = charts.filter(chart => chart?.spanType === target)
        } else if (target?.chart?.spanType != null) {
            charts = charts.filter(chart => chart?.widgetId === target.chart.widgetId)
        } else {
            charts = []
        }

        return charts.filter(chart => chart?.xAxis != null && chart?.widgetId != null)
    }

    /**
     * Check if the marker mode is turned on.
     *
     * @returns `true` if yes, `false` if no.
     */
    public isOn(): boolean {
        return this.currentTarget.length > 0
    }

    /**
     * Creates a draft marker that follows the mouse inside the chart,
     * later can be commited into an actual marker.
     *
     * @param widget Where to apply this draft marker.
     */
    addDraftMarker(widget: Widget | string): void {
        this.__currentTarget = widget

        this.currentTarget.forEach(chart => {
            const value = chart?.hoverPoints?.[0]?.x || 0

            chart.pauseTooltip = true

            // @TODO: Remove the special case of a placeholder marker,
            // now that you change the position of any marker this notion does
            // not apply anymore. This will simplify the code.
            this.chartMarker.addMarker(chart, this.makeMarkerOptions('placeholder', value, chart.widgetId))
        })
    }

    /**
     * Transforms an already commited draft marker (placeholder)
     * back into a uncommited draft. This is useful for altering
     * the position of an already created marker.
     *
     * @param marker marker to be uncommited (transformed into a draft).
     */
    uncommitDraftMarker(marker: MarkersData): void {
        this.__currentTarget = marker.target.widgetId ? { chart: { ...marker.target } } : marker.target.spanType
        this.__editing = marker

        this.currentTarget.forEach(chart => {
            chart.pauseTooltip = true
        })
    }

    /**
     * Updates the position of the draft marker.
     *
     * @param point Where to move the draft marker to.
     */
    moveDraftMarker = (point: { x: number }): void => {
        // @TODO: Check if chart is visible

        const editingId = this.__editing?.id || 'placeholder'

        this.currentTarget.forEach(chart => {
            this.chartMarker.updateMarkerPosition(chart, editingId, { value: point.x })
        })
    }

    /**
     * Transforms the draft marker into an actual marker.
     *
     * @returns A list of the newly created markers.
     */
    commitDraftMarker(): MarkersData[] {
        const now = +new Date()
        const newId = now.toString()

        const editingId = this.__editing?.id || 'placeholder'

        const markers = this.currentTarget
            .map(chart => {
                chart.pauseTooltip = false

                const options = this.chartMarker.findMarker(chart, editingId)!.options

                // Highcharts does not have a TARDIS...
                // Prevent the creation of markers in the future-ish.
                const isValid = chart.spanType !== 'time' || (options?.value != null && options?.value <= now - 2000)

                if (editingId === 'placeholder') {
                    this.chartMarker.removeMarker(chart, editingId)

                    if (isValid) {
                        return this.chartMarker.addMarker(
                            chart,
                            this.makeMarkerOptions(newId, options.value, chart.widgetId)
                        )
                    } else {
                        return null
                    }
                }

                return options
            })
            .map(item => item?.data)
            .filter((item): item is MarkersData => !!item)

        this.__currentTarget = null
        this.__editing = null

        return markers
    }

    /**
     * Removes all draft markers in `this.currentTarget`.
     */
    removeDraftMarker(): void {
        this.currentTarget.forEach(chart => {
            this.chartMarker.removeMarker(chart, 'placeholder')

            chart.pauseTooltip = false
        })

        this.__currentTarget = null
        this.__editing = null
    }

    /**
     * Inserts an already created (saved in the db) marker.
     *
     * @param marker Marker to be inserted
     */
    insertMarker(marker: MarkersData): void {
        const charts = normalizeTarget(marker.target)
        const { id, value, unit, annotationId, target } = marker

        charts.forEach(chart => {
            const widgetId = chart.widgetId

            const data: ExtendedPlotline['options'] = {
                id: id,
                value: value,
                data: {
                    id: id,
                    annotationId: annotationId,
                    target: target,
                    value: value,
                    unit: unit,
                    widgets: Array.from(marker.widgets || []),
                    message: marker.message,
                    markerType: marker.markerType,
                    __rawExtra: marker.__rawExtra
                },
                onShowDetails: this.onShowDetails(target, widgetId),
                onChangePosition: id => null
            }

            if (this.chartMarker.findMarker(chart, id)) {
                this.chartMarker.updateMarker(chart, id, data)
            } else if (chart.series.length > 0) {
                this.chartMarker.addMarker(chart, data)
                this.clearScheduledMarkers(chart.widgetId, data.id)
            } else {
                // Items in axis.plotLinesAndBands can be removed after addPlotline is called here.
                // Therefore, we need to wait until chart has series.length > 0.
                // This seems to be only an issue when loading dashboard page so for now it's only dealt with here.
                this.scheduledMarkers.push([chart.widgetId, data])
            }
        })
    }

    /**
     * Markers can be scheduled to only be inserted after
     * a `chart.addSeries` event occurs.
     *
     * Calling this method will fulfill all requests to
     * insert markers that were postponed.
     */
    insertScheduledMarkers(widgetId: number): void {
        const chart = (Highcharts.charts as Widget['chart'][]).find(c => c?.widgetId === widgetId)

        if (chart == null) return

        const scheduledMarkers = this.scheduledMarkers.filter(([id]) => id === widgetId)

        // There are no scheduled markers.
        // Update all markers in this chart.
        if (scheduledMarkers.length === 0) {
            for (const marker of ChartMarker.getXAxis(chart).plotLinesAndBands) {
                if (marker.timestamp == null) continue

                this.chartMarker.updateMarker(chart, marker.options.id, marker.options)
                Clusters.setHighlight(marker, true)
            }

            return
        }

        scheduledMarkers.forEach(([id, marker]) => {
            this.chartMarker.addMarker(chart, marker)
        })

        this.clearScheduledMarkers(widgetId)
    }

    clearScheduledMarkers(widgetId: number, markerId?: string): void {
        this.scheduledMarkers = this.scheduledMarkers.filter(([widget, marker]) => {
            if (markerId == null) return widget !== widgetId

            return !(widget === widgetId && marker.id === markerId)
        })
    }

    /**
     * Updates a marker.
     *
     * @param marker Marker to be updated
     */
    updateMarker(marker: MarkersData): void {
        const charts = normalizeTarget(marker.target)
        const { id, value, unit, annotationId, target } = marker

        charts.forEach(chart => {
            const widgetId = chart.widgetId

            this.chartMarker.updateMarker(chart, id, {
                id: id,
                value: value,
                data: {
                    id: id,
                    annotationId: annotationId,
                    target: marker.target,
                    value: value,
                    unit: unit,
                    widgets: Array.from(marker.widgets || []),
                    message: marker.message,
                    markerType: marker.markerType,
                    __rawExtra: marker.__rawExtra
                },
                onShowDetails: this.onShowDetails(target, widgetId),
                onChangePosition: () => null,
                isCommited: true
            })

            return this.chartMarker.updateMarkerPosition(chart, id, { value: value })
        })
    }

    /**
     * Finds and remove a given marker.
     *
     * @param marker Marker to be removed
     */
    removeMarker(marker: MarkersData): void {
        const charts = normalizeTarget(marker.target)
        const id = marker.id

        charts.forEach(chart => {
            this.chartMarker.removeMarker(chart, id)

            this.scheduledMarkers = this.scheduledMarkers.filter(([chartId, marker]) => {
                return !(chartId === chart.widgetId && marker.id === id)
            })
        })
    }
}

function normalizeTarget(target: { spanType: string; widgetId?: number }): Widget['chart'][] {
    const charts = (Highcharts.charts as Widget['chart'][]).filter(
        chart => chart?.xAxis != null && chart?.widgetId != null && chart?.spanType != null
    )

    if (target.widgetId == null && target.spanType != null) {
        return charts.filter(chart => chart.spanType === target.spanType)
    } else if (target.widgetId != null && target.spanType != null) {
        return charts.filter(chart => chart.widgetId === target.widgetId)
    }

    return []
}
